package com.wintermar.inspection.feature.domain.model.template

import com.wintermar.inspection.core.ui.component.image.util.FileUploadStatus
import com.wintermar.inspection.feature.domain.model.answer.AnswerModel
import com.wintermar.inspection.feature.domain.model.answer.SummaryAnswerModel
import com.wintermar.inspection.graphql.type.InspectionTemplateStatus

data class InspectionTemplateModel(
    val id: String = "",
    val name: String = "",
    val version: Int = 0,
    val publishedDate: String = "",
    val pages: List<InspectionPageModel> = listOf(),
    val numberOfPages: Int = 0,
    val numberOfSections: Int = 0,
    val numberOfQuestions: Int = 0,
    val status: InspectionTemplateStatus = InspectionTemplateStatus.UNKNOWN__
) {
    fun getFlaggedAnswer(
        predicate: (answer: AnswerModel) -> Boolean = { it.isRequiredFileOrNotes }
    ): List<SummaryAnswerModel> {
        return this.pages.flatMap { page ->
            page.sections.flatMap { section ->
                section.questions
                    .filter { predicate(it.answer) }
                    .map { question ->
                        SummaryAnswerModel(
                            pageTitle = page.title,
                            sectionTitle = section.title,
                            question = question
                        )
                    }
            }
        }
    }

    fun getTotalAnswer(): Int {
        return this.pages.flatMap { it.sections }.sumOf { section ->
            section.questions.count { question -> question.answer.isAnswered(question.answerType) }
        }
    }

    fun getLocation(): String {
        return this.pages[0].sections.flatMap { it.questions }
            .find { it.answer.answerLocation.name.isNotEmpty() }?.answer?.answerLocation?.name
            ?: ""
    }

    fun toSummaryAnswerList(): List<SummaryAnswerModel> {
        return this.pages.flatMap { page ->
            page.sections.flatMap { section ->
                section.questions
                    .map { question ->
                        SummaryAnswerModel(
                            pageTitle = page.title,
                            sectionTitle = section.title,
                            question = question
                        )
                    }
            }
        }
    }

    fun getPageBySection(sectionId: String): InspectionPageModel? {
        return this.pages.find { page -> page.sections.any { it.id == sectionId } }
    }

    fun getPageByQuestion(questionId: String): InspectionPageModel? {
        return this.pages.find { page ->
            page.sections.any { section ->
                section.questions.any { it.id == questionId }
            }
        }
    }

    fun isAllAvailableFilesCompleteUploaded(): Boolean {
        return this.pages.flatMap { page ->
            page.sections.flatMap { section ->
                section.questions.flatMap { question ->
                    question.answer.files.filter { !it.path.startsWith("http") || it.blob != null }
                }
            }
        }
            .all {
                it.uploadStatus == FileUploadStatus.COMPLETE || it.uploadStatus == FileUploadStatus.NONE
            }
    }
}
